<script>
import {mapActions, mapState} from 'vuex';
import { paramCase, camelCase } from 'change-case';
import {callAction, getSignedUrl, notifyUpload, saveApplication, upload} from '@/app/api';
import checkMark from "@/assets/img/icons/check-mark.svg";
import exclamationMark from "@/assets/img/icons/exclamation-mark.svg";
import garbageCan from "@/assets/img/icons/garbage-can.svg";

export default {
	props: {
		application: {
			type: Object,
			default: null
		},
		getApplication: {
			type: Function,
			default: () => {}
		},
	},
	data() {
		return {
			//icons
			checkMark: checkMark,
			exclamationMark: exclamationMark,
			garbageCan: garbageCan,
			//variables
			applicant: {},
			coApps: [],
			isAnySelected: false,
			app: {},
			addingOtherDoc: false,
			documentType: '',
			url: process.env.VUE_APP_ENDPOINT,
			files: [],
			selectedType: 0,
			uploadTypeNames: [
				"Signed Contract",
				"Bill of Sale / Invoice",
				"PAP Form / Void Cheque",
				"Government ID",
				"Completion Certificate",
				"Income Verification"
			],
			uploaded: {},
			maxDocsPerSlot: 10,
			uploadTypes: {
				0: {
					name: 'signedContract',
					fileName: null,
					description: "Signed Contract",
					required: false,
					uploaded: false,
					size: 0,
					documents: [],
				},
				1: {
					name: 'billOfSaleInvoice',
					fileName: null,
					description: 'Bill of Sale / Invoice',
					required: true,
					uploaded: false,
					size: 0,
					documents: [],
				},
				2: {
					name: 'papFormVoidCheque',
					fileName: null,
					description: 'PAP Form / Void Cheque',
					required: true, //this.$route.query.select ? this.$route.query.select : true,
					uploaded: false,
					size: 0,
					documents: [],
				},
				3: {
					name: 'governmentId',
					fileName: null,
					description: 'Government ID',
					required: true,
					uploaded: false,
					size: 0,
					documents: [],
				},
				4: {
					name: 'governmentId2',
					fileName: null,
					description: '2nd Applicant Government ID',
					required: true,
					uploaded: false,
					size: 0,
					documents: [],
				},
				5: {
					name: 'completionCertificate',
					fileName: null,
					description: 'Completion Certificate',
					required: true,
					uploaded: false,
					size: 0,
					documents: [],
				},
				6: {
					name: 'incomeVerification',
					fileName: null,
					description: 'Income Verification',
					required: false,
					uploaded: false,
					size: 0,
					documents: [],
				},
				7: {
					name: 'audioFile',
					fileName: null,
					description: 'Audio File',
					required: false,
					uploaded: false,
					size: 0,
					documents: [],
				}
			},
			isFilesUploaded: false,
			requestUploadTypes: {},
			vModels: {
			},
			uploadProgress: {},
		};

	},

	watch: {

		requestUploadTypes: {
			handler(val) {

				console.log('-------- requestUploadTypes', val);

			},
			immediate: true,
			deep: true
		},

		selectedType: {
			handler(val) {

				console.log('-------- selectedType', val);

			},
			immediate: true,
			deep: true
		}

	},

	async mounted() {
		this.saveLoading(true);
		try {
			this.app = this.application;
			console.log('retrieved app', this.app);
			this.hydrateUploadTypesFromApp();
		} finally {
			this.saveLoading(false);
		}

	},

	computed: {
		...mapState({
			authUser: state => state.auth.authUser,
		}),
		/**
		 * Rows shown on the Upload Files step.
		 *
		 * Completion Certificate (name === 'completionCertificate'):
		 * - Only list this document type after the dealer has chosen **print** on
		 *   CompletionCertificate.vue by clicking **GENERATE PDF FOR PRINTING**, which
		 *   persists `application.completionCertificate.method === 'print'` via the API.
		 * - If no choice yet (`method` is null/undefined), hide the row so the user is not
		 *   asked to upload a CC before visiting the Completion Certificate step.
		 * - If they chose **ROUTE FOR E-SIGNATURE** (`method === 'eSignature'`), the signed
		 *   PDF is handled via SignNow, not as a dealer upload here—so hide the row.
		 */
		uploadTypeRows() {
			const method = this.app.application?.docuseal_completion_certificate_method ?? this.app?.application?.completionCertificate?.method;
			const co = Array.isArray(this.app?.application?.coApps) ? this.app.application.coApps[0] : null;
			const secondApplicantExists = !!(co && (co.firstName || co.lastName || co.name || co.fullName));
			return Object.keys(this.uploadTypes || {})
				.map((k) => ({ idx: k, type: this.uploadTypes[k] }))
				.filter((row) => row.type)
				.filter((row) => {
					let result = true;
					if (row.type.name === 'governmentId2') {
						result = secondApplicantExists;
					} else if (row.type.name === 'completionCertificate') {
						if (this.authUser.udf_hide_coc) {
							result = false;
						} else {
							result = method === 'print';
						}
					}
					return result;
				})
				.sort((a, b) => Number(a.idx) - Number(b.idx))
				.map((row) => {
					if (row.type.name === 'completionCertificate' && method === 'print') {
						return { idx: row.idx, type: { ...row.type, required: true } };
					}
					return row;
				});
		},

	},

	methods: {
		...mapActions([
			'confirm',
			'removeAlerts',
			'saveLoading',
			'showErrorAlert',
			'showInfoAlert',
			'showSuccessAlert',
			'showWarningAlert'
		]),
		/** Compact snapshot for debugging ghost files / API vs UI (filter console by `[UploadFiles]`). */
		uploadTypesDebugSnapshot() {
			const out = {};
			Object.keys(this.uploadTypes || {})
				.sort((a, b) => Number(a) - Number(b))
				.forEach((k) => {
					const s = this.uploadTypes[k];
					if (!s) {
						return;
					}
					out[k] = {
						name: s.name,
						docCount: Array.isArray(s.documents) ? s.documents.length : null,
						docFileNames: Array.isArray(s.documents)
							? s.documents.map((d) => (d && d.fileName) || null)
							: null,
						uploaded: s.uploaded,
						legacyFileName: s.fileName,
						uploadNameSeq: s.uploadNameSeq,
					};
				});
			return out;
		},

		logUploadTrace(phase, extra = null) {
			const base = {
				phase,
				applicationId: this.$route.params.applicationId,
				slots: this.uploadTypesDebugSnapshot(),
			};
			console.log(base, extra, "here")
			console.log('[UploadFiles]', extra != null ? { ...base, extra } : base);
		},

		/**
		 * Map `this.app` (getApplication response) into applicant, uploadTypes,
		 * requestUploadTypes, signed-contract visibility, co-applicant ID slot, and slot normalization.
		 */
		hydrateUploadTypesFromApp() {

			if (!this.app || !this.app.application) {
				return;
			}

			this.applicant = {
				firstName: this.app.application.firstName,
				middleInitial: this.app.application.middleInitial,
				lastName: this.app.application.lastName,
			};

			if (this.app.application.uploadTypes) {
				console.log(this.app.application.uploadTypes, this.uploadTypes);
				const mergedUploadTypes = JSON.parse(JSON.stringify(this.uploadTypes || {}));
				const canonicalIndexByName = {};
				Object.keys(mergedUploadTypes).forEach((k) => {
					const slot = mergedUploadTypes[k];
					if (slot && slot.name) {
						canonicalIndexByName[slot.name] = k;
					}
				});
				Object.keys(this.app.application.uploadTypes).forEach((key) => {
					const incomingSlot = this.app.application.uploadTypes[key];
					const canonicalKey = incomingSlot && incomingSlot.name && canonicalIndexByName[incomingSlot.name] !== undefined
						? canonicalIndexByName[incomingSlot.name]
						: incomingSlot.otherType ? Object.keys(mergedUploadTypes).length : key;
					const mergedSlot = {
						...(mergedUploadTypes[canonicalKey] || {}),
						...(incomingSlot || {})
					};
					// Legacy payloads (all doc types) often omit documents[] entirely.
					// If defaults pre-seed documents: [], keep it unset here so
					// normalizeAllUploadSlots can rebuild documents[] from legacy fields
					// (or keep it empty when no legacy file exists).
					if (
						incomingSlot
						&& !Object.hasOwn(incomingSlot, 'documents')
					) {
						delete mergedSlot.documents;
					}
					// Some payloads still persist legacy upload state (uploaded + fileName) while
					// sending documents: []. In that case, keep documents unset so normalization
					// can rebuild from the legacy fields on page refresh.
					if (
						incomingSlot
						&& Array.isArray(incomingSlot.documents)
						&& incomingSlot.documents.length === 0
						&& typeof incomingSlot.fileName === 'string'
						&& incomingSlot.fileName.trim().length > 0
					) {
						// Some API responses keep fileName but flip uploaded to false.
						// Treat fileName as source-of-truth and let normalization rebuild docs[].
						mergedSlot.uploaded = true;
						delete mergedSlot.documents;
					}
					mergedUploadTypes[canonicalKey] = mergedSlot;
				});
				// De-duplicate by slot name (e.g. completionCertificate can exist at two keys
				// when API and local defaults use different indexes). Prefer API-backed entries.
				const incomingByName = {};
				Object.keys(this.app.application.uploadTypes).forEach((key) => {
					const slot = this.app.application.uploadTypes[key];
					if (slot && slot.name) {
						incomingByName[slot.name] = canonicalIndexByName[slot.name];
					}
				});
				console.log(incomingByName);
				const deduped = {};
				const seenNames = new Set();
				console.log(mergedUploadTypes, "merged?")
				Object.keys(mergedUploadTypes)
					.sort((a, b) => Number(a) - Number(b))
					.forEach((key) => {
						const slot = mergedUploadTypes[key];
						if (!slot) return;
						const slotName = slot.name || `__unnamed_${key}`;
						console.log("KEYS", slotName, key, seenNames)
						if (!seenNames.has(slotName)) {
							deduped[key] = slot;
							seenNames.add(slotName);
							return;
						}
						const preferredKey = incomingByName[slotName];
						console.log("KEYS", preferredKey, slotName, key)
						if (preferredKey !== undefined && String(preferredKey) === String(key)) {
							const existingKey = Object.keys(deduped).find((k) => deduped[k] && deduped[k].name === slotName);
							if (existingKey !== undefined) {
								delete deduped[existingKey];
							}
							// if (this.uploadTypes[key].name !== )
							deduped[key] = slot;
						}
					});
				this.uploadTypes = deduped;
				this.logUploadTrace('hydrate: after merging API uploadTypes into defaults (before signedContract/coApp tweaks)');
			}

			const signNowSentToDealerOnly = !!(["onlyDealer", "print"].includes(this.app.application.docuseal_submission_method) || this.app.application.signNowSentToDealerOnly);

			if (
				this.uploadTypes[0]
				&& this.uploadTypes[0].name == 'signedContract'
				&& !signNowSentToDealerOnly
			) {
				console.log('not dealer only');
				delete this.uploadTypes[0];
				const temp = this.uploadTypes;
				this.uploadTypes = null;
				this.uploadTypes = temp;
				this.selectedType = 1;
			} else {
				this.selectedType = 0;
			}

			if (
				this.uploadTypes[0] == undefined
				&& signNowSentToDealerOnly
			) {
				this.uploadTypes[0] = {
					name: 'signedContract',
					fileName: null,
					description: 'Signed Contract',
					required: true,
					uploaded: false,
					size: 0,
					documents: [],
				};
			}

			if (
				this.app.application.bankVerificationSent != undefined
				&& this.app.application.bankVerificationSent
				&& this.uploadTypes[2]
			) {
				this.uploadTypes[2].required = false;
			}

			if (this.app.application.requestUploadTypes) {
				Object.keys(this.app.application.requestUploadTypes).forEach((element) => {
					this.requestUploadTypes[element] = this.app.application.requestUploadTypes[element]['isRequested'];
				});
			}

			try {
				const co = Array.isArray(this.app.application.coApps) ? this.app.application.coApps[0] : null;
				const secondExists = co && (co.firstName || co.lastName || co.name || co.fullName);
				if (secondExists) {
					const alreadyExists = Object.keys(this.uploadTypes || {}).some(k => this.uploadTypes[k] && this.uploadTypes[k].name === 'governmentId2');
					if (!alreadyExists) {
						this.uploadTypes[4] = {
							name: 'governmentId2',
							fileName: null,
							description: '2nd Applicant Government ID',
							required: true,
							uploaded: false,
							size: 0,
							documents: [],
						};
					}
				}
			} catch (e) {
				console.warn('Could not determine co-applicant existence', e);
			}

			this.logUploadTrace('hydrate: before normalizeAllUploadSlots');
			this.normalizeAllUploadSlots();
			this.logUploadTrace('hydrate: after normalizeAllUploadSlots');
			this.checkBoxChanged();
		},

		async refreshUploadStateFromBackend() {

			const id = this.$route.params.applicationId;
			console.log('[UploadFiles] refreshUploadStateFromBackend: fetching application', id);
			this.app = await this.getApplication();
			this.hydrateUploadTypesFromApp();
		},

		showOtherDocBox() {

			this.addingOtherDoc = true;
			const documentTypeInput = document.getElementById('documentType');
			// console.log('documentTypeInput', documentTypeInput);
			// documentTypeInput.focus();

		},

		checkBoxChanged() {

			console.log('checkBoxChanged');

			this.isAnySelected = false;

			Object.keys(this.uploadTypes).forEach((index, element) => {

				if (
					// ![1, 2].includes(index)
					this.requestUploadTypes[index]
				) {
					this.isAnySelected = true
				}
				// console.log('this.requestUploadTypes[index]', index, element, this.requestUploadTypes[index]);
			});

			// if (this.isAnySelected)

			// console.log('this.isAnySelected', this.isAnySelected);

		},

		async sendLink() {

			this.saveLoading(true);

			let requestUploadTypes = {};
			Object.keys(this.uploadTypes).forEach((index, element) => {

				if (this.uploadTypes[index]['name'] != 'signedContract') {

					requestUploadTypes[index] = {
						name: this.uploadTypes[index]['name'],
						description: this.uploadTypes[index]['description'],
						isRequested: this.requestUploadTypes[index] ?? false,
						required: this.uploadTypes[index]['required'],
						fileName: this.uploadTypes[index]['fileName'],
						uploaded: this.uploadTypes[index]['uploaded'],
						size: this.uploadTypes[index]['size'],
						uploadNameSeq: this.uploadTypes[index].uploadNameSeq,
					}

					this.uploadTypes[index] = {
						name: this.uploadTypes[index]['name'],
						description: this.uploadTypes[index]['description'],
						isRequested: this.requestUploadTypes[index] ?? false,
						required: this.uploadTypes[index]['required'],
						fileName: this.uploadTypes[index]['fileName'],
						uploaded: this.uploadTypes[index]['uploaded'],
						size: this.uploadTypes[index]['size'],
						documents: Array.isArray(this.uploadTypes[index].documents)
							? [...this.uploadTypes[index].documents]
							: [],
						uploadNameSeq: this.uploadTypes[index].uploadNameSeq,
					}

				}

			});

			let saveResult = await saveApplication({
					id: this.$route.params.applicationId,
					uploadTypes: this.uploadTypes,
					requestUploadTypes: requestUploadTypes
			});

			let result = await callAction({
				action: 'sendLink',
				id: this.app.application.id,
				for: 'main',
				domain: window.location.origin + '/',
				dealer: this.authUser.vendorName ?? this.authUser.name,
				purpose: 'uploads'
			})

			if (!result.data.application) {
				await this.showErrorAlert(result.data);
				return;
			}

			await this.showSuccessAlert('Successfully sent link.');
			this.app = null
			this.app = result.data

			console.log('this.app', this.app);
			await this.refreshUploadStateFromBackend();
			this.saveLoading(false);
		},

		async cancelLink() {

			this.saveLoading(true);

			let result = await callAction({
				action: 'cancelLink',
				id: this.app.application.id,
				for: 'main',
				domain: window.location.origin + '/'
			})

			console.log('result', result);

			this.app = null
			this.app = result.data

			let requestUploadTypes = {};

			Object.keys(this.uploadTypes).forEach((index, element) => {

				if (this.uploadTypes[index]['name'] != 'signedContract') {

					requestUploadTypes[index] = {
						name: this.uploadTypes[index]['name'],
						description: this.uploadTypes[index]['description'],
						isRequested: false,
						required: this.uploadTypes[index]['required']
					}

					this.uploadTypes[index] = {
						name: this.uploadTypes[index]['name'],
						description: this.uploadTypes[index]['description'],
						isRequested: false,
						required: this.uploadTypes[index]['required'],
						fileName: this.uploadTypes[index]['fileName'],
						uploaded: this.uploadTypes[index]['uploaded'],
						size: this.uploadTypes[index]['size'],
						documents: Array.isArray(this.uploadTypes[index].documents)
							? [...this.uploadTypes[index].documents]
							: [],
						uploadNameSeq: this.uploadTypes[index].uploadNameSeq,
					}

				}

			});

			let saveResult = await saveApplication({
					id: this.$route.params.applicationId,
					uploadTypes: this.uploadTypes,
					requestUploadTypes: requestUploadTypes
			});


			location.reload()

			this.saveLoading(false);

		},

		async listItems() {

			console.log('listItems');

			console.log('this.uploadTypes', this.uploadTypes);
			console.log('this.uploadTypes keys', Object.keys(this.uploadTypes));
			Object.keys(this.uploadTypes).forEach((index, element) => {
				console.log('index:', index, this.uploadTypes[index]);
				// console.log();
				// console.log('element', this.uploadTypes[element]);
				if (
					this.uploadTypes
					&& this.uploadTypes[index]
					&& this.uploadTypes[index]['uploaded']
				) {
					// console.log('adding', this.uploadTypes[index]);
					this.uploaded[index] = this.uploadTypes[index]
				}
			});

			// console.log('this.uploaded after ', this.uploaded);

		},

		normalizeAllUploadSlots() {
			Object.keys(this.uploadTypes || {}).forEach((k) => {
				const slot = this.uploadTypes[k];
				if (!slot) {
					return;
				}
				if (!Array.isArray(slot.documents)) {
					const initial = (slot.uploaded && slot.fileName)
						? [{ fileName: slot.fileName, size: slot.size || 0 }]
						: [];
					if (initial.length > 0) {
						console.log('[UploadFiles] normalize: created documents[] from legacy fileName/uploaded', {
							key: k,
							name: slot.name,
							legacyFileName: slot.fileName,
						});
					}
					this.$set(slot, 'documents', initial);
				}
				// Do not rebuild documents[] from legacy fileName when documents is already an
				// empty array — that was resurrecting deleted files after save/refresh or when
				// hydrating then uploading another slot (governmentId ghost reappearing).
				this.syncSlotFromDocumentsArray(k);
				this.ensureUploadNameSeq(this.uploadTypes[k]);
			});
			this.uploadTypes = { ...this.uploadTypes };
		},

		/** v6a: `... - {edgeId} - {seq}`; v6b: same then optional extension: `... - {seq}.png` */
		parseUploadNameSequence(fileName) {
			if (!fileName || typeof fileName !== 'string') {
				return 0;
			}
			const m = fileName.match(/ - (\d{2})(\.[^.]+)?$/);
			return m ? parseInt(m[1], 10) : 0;
		},

		maxSequenceFromUploadedDocuments(slot) {
			if (!slot || !Array.isArray(slot.documents) || !slot.documents.length) {
				return 0;
			}
			return slot.documents.reduce((max, d) => {
				const n = this.parseUploadNameSequence(d && d.fileName);
				return n > max ? n : max;
			}, 0);
		},

		ensureUploadNameSeq(slot) {
			if (!slot) {
				return;
			}
			const fromDocs = this.maxSequenceFromUploadedDocuments(slot);
			const cur = Number(slot.uploadNameSeq) || 0;
			const nextVal = Math.max(cur, fromDocs);
			if (nextVal !== cur) {
				this.$set(slot, 'uploadNameSeq', nextVal);
			} else if (fromDocs > 0 && slot.uploadNameSeq === undefined) {
				this.$set(slot, 'uploadNameSeq', nextVal);
			}
		},

		nextSequenceNumberForSlot(index) {
			const slot = this.uploadTypes[index];
			if (!slot) {
				return 1;
			}
			const fromDocs = this.maxSequenceFromUploadedDocuments(slot);
			const stored = Number(slot.uploadNameSeq) || 0;
			return Math.max(stored, fromDocs) + 1;
		},

		syncSlotFromDocumentsArray(key) {
			const slot = this.uploadTypes[key];
			if (!slot || !Array.isArray(slot.documents)) {
				return;
			}
			const before = {
				name: slot.name,
				docCount: slot.documents.length,
				uploaded: slot.uploaded,
				legacyFileName: slot.fileName,
			};
			if (slot.documents.length === 0) {
				slot.uploaded = false;
				slot.fileName = null;
				slot.size = 0;
			} else {
				slot.uploaded = true;
				slot.fileName = slot.documents[0].fileName;
				slot.size = slot.documents[0].size;
			}
			const traceGov = slot.name === 'governmentId' || slot.name === 'governmentId2';
			if (traceGov) {
				console.log('[UploadFiles] syncSlotFromDocumentsArray', { key, before, after: {
						name: slot.name,
						docCount: slot.documents.length,
						uploaded: slot.uploaded,
						legacyFileName: slot.fileName,
					}});
			}
		},

		getDocumentsForSlot(type) {
			if (!type) {
				return [];
			}
			// If documents[] exists (even empty), it is authoritative. Do not fall back to
			// legacy uploaded/fileName — the API often keeps those flags when documents was
			// cleared, which resurrected deleted files (BOS, governmentId, etc.).
			if (Array.isArray(type.documents)) {
				return type.documents;
			}
			if (type.uploaded && type.fileName) {
				console.log('[UploadFiles] getDocumentsForSlot: LEGACY fallback (no documents array)', {
					name: type.name,
					fileName: type.fileName,
				});
				return [{ fileName: type.fileName, size: type.size || 0 }];
			}
			return [];
		},

		documentCountForSlot(index) {
			return this.getDocumentsForSlot(this.uploadTypes[index]).length;
		},

		async removeDocument(slotIndex, docIndex) {
			this.saveLoading(true);
			const slot = this.uploadTypes[slotIndex];
			if (!slot || !Array.isArray(slot.documents)) {
				this.saveLoading(false);
				return;
			}
			console.log('[UploadFiles] removeDocument: start', {
				slotIndex,
				docIndex,
				name: slot.name,
				beforeRemove: this.uploadTypesDebugSnapshot(),
			});
			const newDocuments = slot.documents.filter((_, i) => i !== docIndex);
			this.$set(this.uploadTypes, slotIndex, { ...slot, documents: newDocuments });
			this.syncSlotFromDocumentsArray(slotIndex);
			await saveApplication({
				id: this.$route.params.applicationId,
				uploadTypes: this.uploadTypes
			});
			this.uploadTypes = { ...this.uploadTypes };
			console.log('[UploadFiles] removeDocument: after saveApplication', this.uploadTypesDebugSnapshot());
			await this.refreshUploadStateFromBackend();
			this.saveLoading(false);
		},

		async addUploadType(type) {

			this.saveLoading(true);

			console.log('this.uploadTypes before', this.uploadTypes);

			const numericKeys = Object.keys(this.uploadTypes || {})
				.map((k) => Number(k))
				.filter((n) => Number.isFinite(n));
			const maxKey = numericKeys.length ? Math.max(...numericKeys) : -1;
			const newKey = String(maxKey + 1);

			console.log('new upload slot key', newKey);

			const newSlot = {
				name: camelCase(type),
				description: type,
				otherType: true,
				uploaded: false,
				fileName: null,
				size: 0,
				documents: [],
			};

			// Vue 2: bracket assignment does not make new keys reactive — list would not update.
			this.$set(this.uploadTypes, newKey, newSlot);
			this.$set(this.requestUploadTypes, newKey, false);

			let saveResult = await saveApplication({
					id: this.$route.params.applicationId,
					uploadTypes: this.uploadTypes
			});

			this.addingOtherDoc = false;
			this.documentType = ''

			console.log('this.uploadTypes after', this.uploadTypes);
			await this.refreshUploadStateFromBackend();
			this.saveLoading(false);

		},

		triggerFileInput(id) {
			console.log('triggerFileInput');

			// console.log('this.$refs.fileInput', this.$refs.fileInput);

			let e = document.getElementById(id).click();
			console.log('e', e);


			// this.$refs.fileInput[index].click();
		},

		async handleFileSelect(event, index) {
			const inputEl = event.target;
			const files = Array.from(inputEl.files || []);
			inputEl.value = '';
			if (!files.length) {
				return;
			}

			let remaining = this.maxDocsPerSlot - this.documentCountForSlot(index);
			if (remaining <= 0) {
				await this.showErrorAlert(`You can upload at most ${this.maxDocsPerSlot} files for this document type.`);
				return;
			}

			const toUpload = files.slice(0, remaining);
			if (files.length > remaining) {
				await this.showErrorAlert(`Only the first ${remaining} file(s) were added (maximum ${this.maxDocsPerSlot} per slot).`);
			}

			for (let i = 0; i < toUpload.length; i++) {
				const ok = await this.checkAndUpload(toUpload[i], index);
				if (!ok) {
					break;
				}
			}

			this.uploadTypes = { ...this.uploadTypes };
		},

		async handleFileDrop(event, index) {

			event.preventDefault(); // Prevent default to allow file drop
			event.stopPropagation(); // Stop event bubbling

			const file = event.dataTransfer?.files[0];
			if (!file) {
				return;
			}

			await this.checkAndUpload(file, index);

			this.uploadTypes = { ...this.uploadTypes };

		},

		async checkAndUpload(file, index) {

			// const allowedTypes = [
			// 	'application/pdf',
			// 	'image/jpeg',
			// 	'image/png',
			// 	'image/heic',
			// 	'image/heif',
			// ];

			const allowedAudioTypes = [
				'audio/wav',
				'audio/x-wav',
				'audio/x-m4a'
			]

			if (
				this.uploadTypes[index] && this.uploadTypes[index].name === 'audioFile'
				&& !allowedAudioTypes.includes(file.type)
			) {
				this.showErrorAlert('Invalid file type selected. Only WAV and M4A file formats are accepted in Audio File');
				return false;
			}

			if (
				this.uploadTypes[index] && this.uploadTypes[index].name === 'audioFile'
				&& file.size > 25 * 1024 *1024
			) {
				this.showErrorAlert('File size is too large. Please upload a file smaller than 25MB');
				return false;
			}

			return this.uploadFile(file, index);
		},

		async getUrl(key) {

			if (!key) {
				return;
			}

			this.saveLoading(true)

			let response = await getSignedUrl({
				id: this.$route.params.applicationId,
				key
			});

			let url = JSON.parse(response.body).url

			console.log('url', url);

			window.open(url, '_blank');

			this.saveLoading(false)

		},

		sanitizeUploadBasename(name) {
			if (!name || typeof name !== 'string') {
				return 'file';
			}
			const base = name.replace(/^.*[\\/]/, '');
			return base.replace(/[/\\]/g, '-');
		},

		/** Split sanitized basename so the extension can be placed at the end of the upload key (browser / OS use the final segment as the file type). */
		splitStemAndExt(safeBase) {
			if (!safeBase || typeof safeBase !== 'string') {
				return { stem: 'file', ext: '' };
			}
			const lastDot = safeBase.lastIndexOf('.');
			if (lastDot <= 0 || lastDot >= safeBase.length - 1) {
				return { stem: safeBase, ext: '' };
			}
			return {
				stem: safeBase.slice(0, lastDot),
				ext: safeBase.slice(lastDot),
			};
		},

		countPriorOtherUploadSlots(index) {
			let c = 0;
			Object.keys(this.uploadTypes || {}).forEach((k) => {
				if (
					Number(k) < Number(index)
					&& this.uploadTypes[k]
					&& this.uploadTypes[k].otherType
				) {
					c++;
				}
			});
			return c;
		},

		/**
		 * Uploaded Document Naming v6b (spreadsheet): {prefix}{stem} - {edgeId} - {seq}{ext}
		 * Extension must be last so downloads / “Save as” keep .png, .pdf, etc.
		 * (v6a put the original extension before ` - {edgeId} - {seq}`, which made files look extensionless.)
		 */
		buildUploadedFileName(file, index) {
			const edgeId = this.app.application.edgeId ?? 'XXXX';
			const safeBase = this.sanitizeUploadBasename(file.name);
			const { stem, ext } = this.splitStemAndExt(safeBase);
			const nextSeq = this.nextSequenceNumberForSlot(index);
			const seq = String(nextSeq).padStart(2, '0');
			const type = this.uploadTypes[index];
			const uploadName = type.name;

			if (type.otherType) {
				const ordinal = 11 + this.countPriorOtherUploadSlots(index);
				const custom = String(type.description || uploadName || 'Other').trim().replace(/[/\\]/g, '-');
				return `${ordinal} - ${custom} - ${stem} - ${edgeId} - ${seq}${ext}`;
			}

			const prefixByName = {
				signedContract: '1 - CONTRACT - ',
				billOfSaleInvoice: '2 - BOS - ',
				governmentId: '3 - ID - ',
				governmentId2: '3 - ID2 - ',
				papFormVoidCheque: '4 - VC - ',
				completionCertificate: '5 - CC - ',
				incomeVerification: '9 - POI - ',
				audioFile: '10 - AUD - ',
			};

			const prefix = prefixByName[uploadName];
			if (prefix) {
				return `${prefix}${stem} - ${edgeId} - ${seq}${ext}`;
			}

			return `99 - OTH - ${stem} - ${edgeId} - ${seq}${ext}`;
		},

		async uploadFile(file, index) {

			if (!file) {
				console.warn("No files were dropped");
				return false;
			}

			if (this.documentCountForSlot(index) >= this.maxDocsPerSlot) {
				await this.showErrorAlert(`You can upload at most ${this.maxDocsPerSlot} files for this document type.`);
				return false;
			}

			const slot = this.uploadTypes[index];
			if (!Array.isArray(slot.documents)) {
				this.$set(slot, 'documents', []);
			}

			this.saveLoading(true);

			const fileName = this.buildUploadedFileName(file, index);
			const id = this.$route.params.applicationId;
			const payload = {
				contentType: file.type,
				fileName: fileName,
				id: id,
				uploadType: slot.name,
			};
			const result = await upload(file, payload);
			if (result.status !== 200) {
				await this.showSuccessAlert(`Something went wrong while uploading, Error: ${JSON.stringify(result.data)}`);
				this.saveLoading(false);
				return false;
			}

			slot.documents.push({ fileName, size: file.size });
			this.syncSlotFromDocumentsArray(index);
			const seqUsed = this.parseUploadNameSequence(fileName);
			if (seqUsed > 0) {
				this.$set(slot, 'uploadNameSeq', seqUsed);
			}

			// Avoid clobbering concurrent customer uploads: merge this slot change
			// into a fresh backend snapshot before persisting.
			const fresh = await this.getApplication();
			const latestUploadTypes = JSON.parse(JSON.stringify((fresh && fresh.application && fresh.application.uploadTypes) || {}));
			const currentType = this.uploadTypes[index];
			const currentName = currentType && currentType.name;
			const remoteKeyByName = Object.keys(latestUploadTypes).find(
				(k) => latestUploadTypes[k] && latestUploadTypes[k].name === currentName
			);
			const targetKey = remoteKeyByName !== undefined ? remoteKeyByName : String(index);
			const remoteSlot = latestUploadTypes[targetKey] || {};
			const remoteDocuments = Array.isArray(remoteSlot.documents)
				? remoteSlot.documents.slice()
				: [];
			const mergedDocuments = remoteDocuments.slice();
			const alreadyExists = mergedDocuments.some((d) => d && d.fileName === fileName);
			if (!alreadyExists) {
				mergedDocuments.push({ fileName, size: file.size });
			}
			const nextUploadNameSeq = Math.max(
				Number(remoteSlot.uploadNameSeq) || 0,
				Number(currentType && currentType.uploadNameSeq) || 0,
				seqUsed || 0
			);
			latestUploadTypes[targetKey] = {
				...remoteSlot,
				...currentType,
				documents: mergedDocuments,
				uploaded: mergedDocuments.length > 0,
				fileName: mergedDocuments.length > 0 ? mergedDocuments[0].fileName : null,
				size: mergedDocuments.length > 0 ? (mergedDocuments[0].size || 0) : 0,
				uploadNameSeq: nextUploadNameSeq > 0 ? nextUploadNameSeq : undefined,
			};

			await saveApplication({
				id: id,
				uploadTypes: latestUploadTypes
			});
			let type = currentType.description ?? currentType.name;
			const currentTypes = ["signedContract", "billOfSaleInvoice", "governmentId", "governmentId2", "papFormVoidCheque", "completionCertificate", "incomeVerification", "audioFile"]; //TODO: temporary workaround for uploadTypes not be a set lookup
			if (!currentTypes.includes(currentType.name)) {
				type += ' (Other)';
			}
			await notifyUpload(id, type);
			try {
				await this.refreshUploadStateFromBackend();
				this.logUploadTrace('uploadFile: after refreshUploadStateFromBackend', {
					uploadedSlotIndex: index,
					uploadedType: this.uploadTypes[index] && this.uploadTypes[index].name,
				});
			} catch (err) {
				console.warn('UploadFiles: refresh after upload failed', err);
				this.uploadTypes = { ...this.uploadTypes };
			}

			this.saveLoading(false);

			return true;
		},

		paramCase: val => paramCase(val),

		async onSubmit(e) {

			console.log('onSubmit');

			// this.addUploadType(this.documentType)

		},

		formatSizeUnits(bytes) {

			bytes = parseFloat(bytes)

			if (bytes >= 1073741824) { bytes = (bytes / 1073741824).toFixed(2) + " GB" }
			else if (bytes >= 1048576) { bytes = (bytes / 1048576).toFixed(2) + " MB" }
			else if (bytes >= 1024) { bytes = (bytes / 1024).toFixed(2) + " KB" }
			else if (bytes >= 1) { bytes = "1 KB" }
			else { bytes = "0" }

			return bytes

		}

	}

};
</script>

<template>
	<div class="upload-template">
		<div class="container-xl">
			<div class="row">
				<div class="col-24">
					<div class="template__page-title">
						<h1>Upload Files</h1>
					</div>
					<div class="template__page-description mb-5">
						<p>
							Upload supporting documentation by selecting each document chiclet and selecting Choose File. Use Add
							Other Document to specify additional optional documents. Use the check boxes and then select “Send Link”
							to send customers a link to upload selected documents.
						</p>
					</div>
				</div>
			</div>
			<form @submit="() => {}" novalidate>
				<div class="container-xl template--vh">
					<div class="row">
						<div class="col-lg-24 mb-5">
							<div v-for="row in uploadTypeRows" :key="row.idx" class="tffi-upload-card-mini d-flex flex-row my-1">
								<!-- Checkbox Section -->
								<div class="d-flex align-items-center me-1 me-lg-4">
									<input
										:style="(app.application && app.application['linkSentOn'] && app.application['linkPurpose'] == 'uploads') ? 'opacity: 0.4' : ''"
										:disabled="(app.application && app.application['linkSentOn'] && app.application['linkPurpose'] == 'uploads')"
										v-if="!['signedContract', 'billOfSaleInvoice', 'completionCertificate', 'audioFile', 'governmentId2'].includes(row.type.name) && !row.type.uploaded"

										v-on:change="checkBoxChanged()" v-model="requestUploadTypes[row.idx]" type="checkbox"
										:name="requestUploadTypes[row.idx]" :id="requestUploadTypes[row.idx]"
										class="tffi-input-checkbox theming primary-color">
									<div
										v-if="['signedContract', 'billOfSaleInvoice', 'completionCertificate', 'audioFile', 'governmentId2'].includes(row.type.name) || row.type.uploaded"
										class="tffi-input-checkbox theming primary-color p-2 me-2 invisible"></div>
								</div>
								<!-- Upload Type Card -->
								<div class="flex-grow-1">
									<div class="tffi-card tffi-card-secondary-card d-flex flex-column flex-lg-row p-2 p-lg-4 upload-type">
										<div class="flex-grow-1 d-flex flex-column min-w-0">
											<!-- Description row: text (width-limited on lg) + status icons aligned right -->
											<div
												class="d-flex align-items-start align-items-lg-center w-100 flex-wrap gap-2 min-w-0 upload-type-header-row"
												:class="{ 'upload-type-header-row--has-files': getDocumentsForSlot(row.type).length > 0 }">
												<div class="upload-description flex-grow-1 min-w-0">
													<div class="d-flex align-items-center flex-wrap gap-2">
														<p class="my-0 flex-grow-1 min-w-0">
															{{ row.type.description }} <span v-if="row.type.required" class="text-danger">*</span>
														</p>
														<div
															v-if="requestUploadTypes[row.idx] && (app.application && app.application['linkSentOn'] && app.application['linkPurpose'] == 'uploads') && !row.type.uploaded"
															class="tffi-card-secondary-card__right-area__link-confirmation-area position-static flex-shrink-0">
															<ul class="float-end mb-0">
																<li>Link Sent</li>
															</ul>
														</div>
													</div>
												</div>
												<div class="upload-status-icons d-flex align-items-center flex-shrink-0 ms-auto">
													<div v-if="row.type.uploaded" class="status-accepted p-1 p-lg-2">
														<img alt="Uploaded" class="upload-icon-img" :src="checkMark" >
													</div>
													<div v-if="row.type.required && !row.type.uploaded" class="status-denied px-1 px-lg-2">
														<img alt="Not Uploaded" class="upload-icon-img" :src="exclamationMark" >
													</div>
												</div>
											</div>
											<!-- Per-document rows (backend filenames) -->
											<ul v-if="getDocumentsForSlot(row.type).length" class="upload-doc-list list-unstyled mb-0 mt-2">
												<li
													v-for="(doc, di) in getDocumentsForSlot(row.type)"
													:key="row.idx + '-' + doc.fileName + '-' + di"
													class="upload-doc-row d-flex align-items-center flex-wrap py-1 gap-1">
													<span class="upload-doc-name flex-grow-1 text-break small">{{ doc.fileName }}</span>
													<div class="upload-doc-actions d-flex align-items-center flex-shrink-0">
														<button
															type="button"
															class="upload-doc-icon-btn cursor-pointer"
															@click.prevent="getUrl(doc.fileName)"
															title="Preview">
															<i class="fa-regular fa-eye"></i>
														</button>
														<span class="upload-doc-size small text-nowrap text-muted">{{ formatSizeUnits(doc.size) }}</span>
														<button
															type="button"
															class="upload-doc-icon-btn"
															@click.prevent="removeDocument(row.idx, di)"
															title="Remove file">
															<img alt="Delete" class="upload-icon-img" :src="garbageCan" >
														</button>
													</div>
												</li>
											</ul>
										</div>
										<!-- Vertical Line -->
										<div class="mx-3 d-none d-lg-block" style="border-left: 2px solid lightgray;"></div>
										<!-- Drag & Drop + Choose Files -->
										<div class="d-flex align-items-center upload-actions ms-lg-5- mt-2 mt-lg-0">
											<div
												class="tffi-card tffi-card-upload-card theming primary-color text-center rounded-0 drag-drop-area"
												@dragover.prevent
												@drop="handleFileDrop($event, row.idx)">
												<p class="tffi-card-upload-card__title theming primary-color mb-0 drag-drop-text">
													Drag & Drop File
												</p>
											</div>
											<input
												:accept="uploadTypes[row.idx] && uploadTypes[row.idx].name === 'audioFile' ? 'audio/wav,audio/x-m4a' : 'image/png, image/jpeg, application/pdf, image/heic, image/heif'"
												:id="'fileInput'+row.idx" type="file" multiple @change="handleFileSelect($event, row.idx)" hidden>
											<button
												class="tffi-btn tffi-btn--solid tffi-btn--bold theming accent-color primary-color-btn-solid ms-2 ms-lg-3 choose-file-btn"
												@click.prevent="triggerFileInput('fileInput' + row.idx)">
												Choose Files
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div class="col-lg-12">
							<button @click.prevent="showOtherDocBox()" v-if="!addingOtherDoc"
							        class="tffi-btn tffi-btn--solid tffi-btn--bold theming accent-color primary-color-btn-solid w-100">
								Add Other Document
							</button>
							<div v-if="addingOtherDoc" class="tffi-card tffi-card-secondary-card other-document-card">
								<div class="row">
									<div class="col-24">
										<h2>
											Other Document
										</h2>
										<div class="d-flex- flex-column- w-100">
											<label>Document Type</label>
											<input id="documentType" v-model="documentType" @keyup.enter="addUploadType(documentType)"
											       type="text" class="w-100">
										</div>
										<div class="px-4"></div>
										<div class="d-flex justify-content-between">
											<button @click.prevent="addingOtherDoc = false"
											        class="tffi-btn tffi-btn--solid tffi-btn--bold theming accent-color primary-color-btn-solid px-5">
												Cancel
											</button>
											<button @click.prevent="addUploadType(documentType)"
											        class="tffi-btn tffi-btn--solid tffi-btn--bold theming accent-color primary-color-btn-solid px-5 float-right">
												Add
											</button>
										</div>
										<div class="d-inline-flex- align-items-right">
										</div>
									</div>
								</div>
							</div>
							<div class="template--section-divider"></div>
							<hr v-if="!addingOtherDoc" class="my-5">
							<div v-if="!addingOtherDoc" class="tffi-card tffi-card-main-card theming secondary-color">
								<div class="row">
									<div class="col-24 col-sm-14 col-md-14 col-lg-14 col-xl-14 d-flex align-items-center mb-4 mb-sm-0">
										<p v-if="app && app.application && app.application['linkSentOn'] && app.application['linkPurpose'] == 'uploads'"
										   class="my-0">A link has been sent to the primary applicant to upload supporting documents.</p>
										<p v-else class="my-0">
											Select documents and click "Send Link" to email the primary applicant a link to upload
											supporting documents.
										</p>
									</div>
									<div
										class="col-24 col-sm-5 col-md-5 col-lg-5 col-xl-5 d-flex align-items-center justify-content-center justify-content-xl-end mb-3 mb-sm-0">
										<button type="button"
										        aclass="tffi-btn tffi-btn--solid theming accent-color accent-color-btn-solid mx-auto-lg w-100 text-nowrap-"
										        class="tffi-btn tffi-btn--solid tffi-btn--bold theming accent-color primary-color-btn-solid ms-3"
										        v-if="app && app.application && app.application['linkSentOn'] && app.application['linkPurpose'] == 'uploads'"
										        @click.prevent="cancelLink()">
											Cancel Link
										</button>
									</div>
									<div
										class="col-24 col-sm-5 col-md-5 col-lg-5 col-xl-5 d-flex align-items-center justify-content-center justify-content-xl-end mb-0 mb-sm-0">
										<button :disabled="!this.isAnySelected" @click.prevent="sendLink()"
										        aclass="tffi-btn tffi-btn--solid theming primary-color primary-color-btn-solid mx-auto-lg w-100 text-nowrap"
										        class="tffi-btn tffi-btn--solid tffi-btn--bold theming accent-color primary-color-btn-solid ms-3">
											{{ (app && app.application && app.application['linkSentOn'] && app.application['linkPurpose'] ==
											'uploads') ? 'Resend Link' : 'Send Link' }}
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</form>
		</div>
	</div>
</template>

<style lang="scss" scoped>
	.upload-type {
		// cursor: pointer;

		&.active {
			border: solid #C18F14 2px
		}

	}

	.tffi-card-upload-card {
		min-height: 0;
	}

	.cursor-pointer {
		cursor: pointer;
	}

	.upload-description {
		font-size: 12px;
		flex-basis: 100%;
	}

	.upload-type-header-row--has-files {
		border-bottom: 1px solid #d1d5db;
		padding-bottom: 0.5rem;
		margin-bottom: 0.125rem;
	}

	.upload-status-icons {
		gap: 0.35rem;
	}

	.upload-doc-name {
		color: #6b7280;
	}

	.upload-doc-actions {
		gap: 0.5rem;
		margin-left: auto;
	}

	.upload-doc-icon-btn {
		border: none;
		background: none;
		padding: 0.35rem 0.5rem;
		cursor: pointer;
		line-height: 1;
		color: inherit;
	}

	.upload-doc-icon-btn .fa-eye {
		opacity: 0.85;
	}

	.upload-actions {
		width: 100%;
	}

	/* Mobile-first: compact sizes */
	.template__page-description p {
		font-size: 11px;
	}

	.tffi-input-checkbox {
		transform: scale(0.75);
	}

	.upload-description p {
		font-size: 10px;
	}

	.upload-icon-img {
		width: 14px;
		height: 14px;
	}

	.upload-file-size {
		width: auto;
		font-size: 11px;
	}

	.drag-drop-area {
		width: 90%;
		min-height: 40px;
		padding: 4px;
	}

	.drag-drop-text {
		font-size: 11px;
	}

	.choose-file-btn {
		height: 40px;
		padding: 4px 8px;
		font-size: 11px;
	}

	.min-w-0 {
		min-width: 0;
	}

	.upload-doc-list {
		width: 100%;
	}

	.upload-doc-row .upload-doc-name {
		min-width: 0;
	}

	/* Desktop: restore full sizes */
	@media (min-width: 992px) {
		.template__page-description p {
			font-size: inherit;
		}

		.tffi-input-checkbox {
			transform: none;
		}

		.upload-description {
			flex: 0 1 50%;
			max-width: 50%;
			min-width: 0;
			overflow: hidden;
		}

		.upload-description p {
			font-size: inherit;
		}

		.upload-actions {
			width: 50%;
		}

		.upload-status-icons {
			gap: 0.5rem;
		}

		.upload-icon-img {
			width: auto;
			height: auto;
		}

		.upload-file-size {
			width: 100px;
			font-size: inherit;
		}

		.drag-drop-area {
			min-height: 60px;
			padding: 8px;
		}

		.drag-drop-text {
			font-size: 14px;
		}

		.choose-file-btn {
			height: 60px;
			padding: 8px 12px;
			font-size: inherit;
		}
	}

</style>
